import React, {FC, useCallback, useMemo} from 'react';

import {useLocalize} from '../../../../../../libs/shared/core/src/helpers/internationalization/internationalizationContext';
import {
  defineMessage,
  Trans,
} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {Button} from '../../../../../../libs/shared/ui/src/components/buttons/button';
import {ButtonVariantsEnum} from '../../../../../../libs/shared/ui/src/components/buttons/buttonConstants';
import {Dropdown} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdown';
import {DropdownHeader} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownHeader';
import {DropdownVirtualList} from '../../../../../../libs/shared/ui/src/components/dropdowns/dropdownVirtualList';
import {DropdownItem} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItem';
import {DropdownItemCheckbox} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemCheckbox';
import {DropdownItemContent} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemContent';
import {DropdownItemIcon} from '../../../../../../libs/shared/ui/src/components/dropdowns/items/dropdownItemIcon';
import {PopoverTransferProps} from '../../../../../../libs/shared/ui/src/components/popovers/popoverCoordinator';
import {VisualSizesEnum} from '../../../../../../libs/shared/ui/src/styles/commonStyles';
import {ModifiersEnum} from '../../../../../../srcCommon/inboxes/search/searchModifiersHelpers';
import {searchScopeDropdownItems} from '../../searchBar/queryBuilder/inboxesSearchQueryBuilderHelpers';
import {SearchHintClickTrackingOptions} from '../../searchTrackingHelpers';

const defaultSearchDropdownWidth = 226;

/*
 * Props.
 */

interface SearchHintInDropdownProps {
  popoverProps: PopoverTransferProps;
  onClick: (
    modifier: ModifiersEnum,
    value: string,
    trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>,
  ) => void;
  onClear: (modifier: ModifiersEnum, trackingOptions: Pick<SearchHintClickTrackingOptions, 'type'>) => void;
  selectedValues: readonly string[];
}

/*
 * Component.
 */

export const SearchHintInDropdown: FC<SearchHintInDropdownProps> = (props) => {
  const {onClick, onClear, selectedValues, popoverProps} = props;

  const isValueSelected = useCallback(
    (value: string) => selectedValues.some((e) => e === value),
    [selectedValues],
  );

  const searchScopes = useMemo(() => searchScopeDropdownItems(), []);

  const handleToggle = useCallback(
    (value: string) => {
      onClick(ModifiersEnum.IN, value, {type: ModifiersEnum.IN});
    },
    [onClick],
  );

  const localize = useLocalize();
  const headerTitle = localize(
    defineMessage({
      id: `search-hint-in-dropdown-header-title`,
      message: 'In',
    }),
  );

  return (
    <Dropdown {...popoverProps} width={defaultSearchDropdownWidth}>
      <DropdownHeader
        title={headerTitle}
        rightIcon={
          selectedValues.length > 0 ? (
            <Button
              variant={ButtonVariantsEnum.LINK}
              size={VisualSizesEnum.SMALL}
              onClick={() => {
                onClear(ModifiersEnum.IN, {type: ModifiersEnum.IN});
                popoverProps.onRequestClose();
              }}
            >
              <Trans id="clear-refinement-button-link">Clear</Trans>
            </Button>
          ) : null
        }
      />
      <DropdownVirtualList
        width={defaultSearchDropdownWidth}
        maxHeight={300}
        rowCount={searchScopes.length}
        renderRow={(index) => (
          <DropdownItem
            onSelect={() => {
              handleToggle(searchScopes[index].inputValue.value);
            }}
          >
            <DropdownItemIcon
              iconName={searchScopes[index].icon}
              iconSize={16}
              label={searchScopes[index].label}
            />
            <DropdownItemContent>{searchScopes[index].label}</DropdownItemContent>
            <DropdownItemCheckbox isChecked={isValueSelected(searchScopes[index].inputValue.value)} />
          </DropdownItem>
        )}
      />
    </Dropdown>
  );
};
